import React from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Server, Cpu, Layers, RefreshCw } from 'lucide-react';
import { useInfraHealthOverview } from './hooks/useInfraHealth';
import RoomSensorCard from './components/RoomSensorCard';
import ProxmoxNodeCard from './components/ProxmoxNodeCard';
import ClusterResourcesCard from './components/ClusterResourcesCard';
import CephCard from './components/CephCard';
import ProxmoxNodesTable from './components/ProxmoxNodesTable';
import VmStatusTable from './components/VmStatusTable';
import ServerGrid from './components/ServerGrid';
import FleetSummary from './components/FleetSummary';
import SectionHeader from './components/SectionHeader';
import './InfraHealth.css';

export default function InfraHealth() {
    const { data, loading, error, refresh } = useInfraHealthOverview();

    if (loading && !data) {
        return (
            <Box className="ihealth-root ihealth-loading">
                <CircularProgress />
                <Typography sx={{ mt: 2, opacity: 0.75 }}>Loading infra health…</Typography>
            </Box>
        );
    }

    const room = data?.room;
    const pve = data?.proxmox;
    const hostsBlock = data?.hosts;
    const hosts = hostsBlock?.hosts || [];
    const pveNodes = pve?.nodes || [];
    const pveVms = pve?.vms || [];
    const updatedAt = data?.generatedAt ? new Date(data.generatedAt * 1000) : null;

    return (
        <Box className="ihealth-root">
            <Box className="ihealth-title-row">
                <Box>
                    <Typography className="ihealth-title">Infra Health</Typography>
                    <Typography className="ihealth-subtitle">
                        Proxmox cluster · Zabbix fleet · Broadlink RM4 sensor
                        {updatedAt && <> · updated {updatedAt.toLocaleTimeString()}</>}
                    </Typography>
                </Box>
                <button className="ihealth-refresh-btn" onClick={refresh} title="Refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </Box>

            {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Failed to load: {error.message || 'unknown error'}
                </Alert>
            )}

            <FleetSummary hosts={hosts} proxmoxNodes={pveNodes} vms={pveVms} />

            {/* Row: Room sensor + Cluster summary (left) · Proxmox nodes (right) */}
            <Box className="ihealth-grid ihealth-grid--top">
                <Box className="ihealth-grid__room" sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
                    <RoomSensorCard room={room} />
                    {pve?.available && pve.cluster && <ClusterResourcesCard cluster={pve.cluster} />}
                    {pve?.available && <CephCard ceph={pve.ceph} />}
                    {pve?.available && pveNodes.length > 0 && <ProxmoxNodesTable nodes={pveNodes} />}
                </Box>
                <Box className="ihealth-grid__pve">
                    <SectionHeader
                        icon={<Server size={18} />}
                        title="Proxmox Nodes"
                        subtitle={pve?.available ? `${pveNodes.length} node${pveNodes.length === 1 ? '' : 's'}` : 'not configured'}
                    />
                    {pve?.available ? (
                        <Box className="ihealth-pve-nodes">
                            {pveNodes.map(n => (
                                <ProxmoxNodeCard key={n.node} node={n} />
                            ))}
                            {pveNodes.length === 0 && (
                                <Box className="ihealth-empty">No Proxmox nodes discovered.</Box>
                            )}
                        </Box>
                    ) : (
                        <Box className="ihealth-empty">
                            Proxmox API is not configured.<br />
                            Set <code>PVE_API_HOST</code>, <code>PVE_API_TOKEN_ID</code>, <code>PVE_API_TOKEN_SECRET</code> on the backend.
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Row: VM status */}
            {pve?.available && (
                <Box sx={{ mt: { xs: 2, md: 2.5 } }}>
                    <SectionHeader
                        icon={<Layers size={18} />}
                        title="Cluster VMs & Containers"
                        subtitle="Live status from Proxmox"
                    />
                    <VmStatusTable vms={pveVms} />
                </Box>
            )}

            {/* Row: Zabbix hosts */}
            <Box sx={{ mt: { xs: 2, md: 2.5 } }}>
                <SectionHeader
                    icon={<Cpu size={18} />}
                    title="Server Health (Zabbix)"
                    subtitle={hostsBlock?.available ? `${hosts.length} host${hosts.length === 1 ? '' : 's'} monitored` : 'not configured'}
                />
                {hostsBlock?.available ? (
                    <ServerGrid hosts={hosts} />
                ) : (
                    <Box className="ihealth-empty">
                        Zabbix API is not configured.<br />
                        Set <code>ZABBIX_API_URL</code> and <code>ZABBIX_API_TOKEN</code> on the backend.
                    </Box>
                )}
            </Box>
        </Box>
    );
}
